import { Routes, Route, Navigate } from 'react-router-dom';
import { SquircleNoScript } from '@squircle-js/react';

// Layouts
import DashboardLayout from './layouts/DashboardLayout';
import AuthLayout from './layouts/AuthLayout';
import PublicLayout from './layouts/PublicLayout';

// Public Pages
import Home from './pages/public/Home';
import Pricing from './pages/public/Pricing';
import Privacy from './pages/public/Privacy';
import FontTest from './pages/FontTest';
import Admin from './pages/dashboard/Admin';
import Terms from './pages/public/Terms';
import Swabs from './pages/public/Swabs';

// Auth Pages
import SignIn from './pages/auth/SignIn';
import SignUp from './pages/auth/SignUp';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import VerifyEmailConfirm from './pages/auth/VerifyEmailConfirm';

// Onboarding Pages
import SelectPlan from './pages/onboarding/SelectPlan';
import WorkspaceSetup from './pages/onboarding/WorkspaceSetup';

// Dashboard Pages
import Campaigns from './pages/dashboard/Campaigns';
import CampaignEdit from './pages/dashboard/campaigns/CampaignEdit';
import EmailAccounts from './pages/dashboard/EmailAccounts';
import AddEmailAccount from './pages/dashboard/AddEmailAccount';
import DfySetup from './pages/dashboard/DfySetup';
import DfySuccess from './pages/dashboard/DfySuccess';
import Mailbox from './pages/dashboard/Mailbox';
import Guides from './pages/dashboard/Guides';
import GuideReader from './pages/dashboard/GuideReader';
import FindPeople from './pages/dashboard/FindPeople';
import { FIND_PEOPLE_ENABLED } from './lib/config';
import Contacts from './pages/dashboard/Contacts';
import ContactListDetail from './pages/dashboard/ContactListDetail';
import { RenamedPath } from './components/RenamedPath';
import Reports from './pages/dashboard/Reports';
import Agents from './pages/dashboard/Agents';
import AgentWizard from './pages/dashboard/AgentWizard';
import Agent from './pages/dashboard/Agent';
import AgentEdit from './pages/dashboard/AgentEdit';
import World from './pages/dashboard/World';
import Subscription from './pages/dashboard/Subscription';
import WorkspaceSettings from './pages/dashboard/WorkspaceSettings';

// Components
import ProtectedRoute from './components/ProtectedRoute';

function App() {
  return (
    <>
      <Routes>
        {/* Public pages */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/swabs" element={<Swabs />} />
        </Route>

        <Route path="/font-test" element={<FontTest />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/login" element={<Navigate to="/sign-in" replace />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-email/confirm" element={<VerifyEmailConfirm />} />
        </Route>

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          {/* Full-screen flows (no dashboard layout) */}
          <Route path="/onboarding/select-plan" element={<SelectPlan />} />
          <Route path="/onboarding/workspace-setup" element={<WorkspaceSetup />} />

          <Route element={<DashboardLayout />}>
            <Route path="/dashboard/agent" element={<Agent />} />
            <Route path="/dashboard/agents/new" element={<AgentWizard />} />
            <Route path="/dashboard" element={<Navigate to="/dashboard/campaigns" replace />} />
            <Route path="/dashboard/campaigns" element={<Campaigns />} />
            <Route path="/dashboard/campaigns/:id/edit" element={<CampaignEdit />} />
            <Route path="/dashboard/email-accounts" element={<EmailAccounts />} />
            <Route path="/dashboard/email-accounts/add" element={<AddEmailAccount />} />
            <Route path="/dashboard/email-accounts/dfy" element={<DfySetup />} />
            <Route path="/dashboard/email-accounts/dfy/success" element={<DfySuccess />} />
            <Route path="/dashboard/mailbox" element={<Mailbox />} />
            <Route path="/dashboard/guides" element={<Guides />} />
            <Route path="/dashboard/guides/:slug" element={<GuideReader />} />
            {FIND_PEOPLE_ENABLED && (
              <>
                <Route path="/dashboard/find-leads" element={<FindPeople />} />
                {/* Old address, from before the Find Leads rename. */}
                <Route path="/dashboard/find-people/*" element={<RenamedPath from="/dashboard/find-people" to="/dashboard/find-leads" />} />
              </>
            )}
            <Route path="/dashboard/lists" element={<Contacts />} />
            <Route path="/dashboard/lists/:listId" element={<ContactListDetail />} />
            {/* Old addresses, from before the Lists rename. */}
            <Route path="/dashboard/contacts/*" element={<RenamedPath from="/dashboard/contacts" to="/dashboard/lists" />} />
            <Route path="/dashboard/agents" element={<Agents />} />
            <Route path="/dashboard/agents/:id" element={<AgentEdit />} />
            <Route path="/dashboard/reports" element={<Reports />} />
            <Route path="/dashboard/world" element={<World />} />
            <Route path="/dashboard/subscription" element={<Subscription />} />
            <Route path="/dashboard/workspace" element={<WorkspaceSettings />} />
            <Route path="/dashboard/admin" element={<Admin />} />
          </Route>
        </Route>
        
        {/* 404 - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <SquircleNoScript />
    </>
  );
}

export default App;
